<template>
  <div>
    <h2 class="mb-5">{{ defaultHeader }}</h2>

    <!-- New invoice -->
    <NewInvoiceModal
      v-if="isNewInvoiceModalShown"
      :landlordId="landlordId"
      :invoiceType="newInvoiceType"
      :tenancyId="tenancyId"
      @order-created="fetchTenancyOrders(false)"
    />

    <hl-Toast
      v-if="getTenancy.subscription.subscriptionPlanName === 'Garantert'"
      :title="translate('tenancy.ddg.warning.title')"
      :text="translate('tenancy.ddg.warning.md-text')"
      variant="warning"
      class="mt-4"
    />

    <!-- Payments status -->
    <div v-if="isFree" class="mt-3">
      <BlockContainer class="p-5">
        <h5>Eksisterende fakturaer</h5>

        <TenancyAccountingOrders
          :landlord-id="Number(landlordId)"
          :tenancy-id="Number(tenancyId)"
          :orders="orders"
          :is-fetching-initial-orders="isFetchingInitialOrders"
          :is-order-list-expanded="isOrderListExpanded"
          :is-fetching-subsequent-orders="isFetchingSubsequentOrders"
          is-subscription-free
          class="mt-5"
          @fetch-orders="fetchTenancyOrders"
        />
      </BlockContainer>

      <BlockContainer class="p-5 mt-4">
        <h5 class="m-0 d-inline-block">Betalingsoversikt</h5>

        <div class="mt-5 d-flex flex-column align-items-center">
          <h5>Oppgrader for å få automatisk innkreving av husleien</h5>

          <img src="@/assets/empty-interest-management.svg" width="140" height="130" class="mt-4" />

          <p class="upgrade-lead mt-5 text-center">
            Få husleien på konto til rett tid - vi gjør jobben for deg! Automatisk innkreving, purring og inkasso av
            husleien er inkludert i alle våre abonnement.
            <a href="https://www.husleie.no/utleier/innkreving-av-husleien" target="_blank" rel="noopener">Les mer</a>
          </p>

          <UpdateSubscriptionButton
            :landlord-id="landlordId"
            :tenancy-id="tenancyId"
            origin="dashboard_tenancyview_payments"
          />
        </div>
      </BlockContainer>
    </div>

    <div v-else class="mt-3">
      <BlockContainer class="p-5">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="m-0 d-inline-block">Betalingsoversikt</h5>

          <div class="text-right">
            <template v-if="getTenancy.subscription.subscriptionPlanName !== 'Garantert' || isUserSudo">
              <hl-Dropdown class="btn-group" direction="right">
                <div slot="trigger">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary dropdown-toggle"
                    :class="{
                      dropdownSudo: getTenancy.subscription.subscriptionPlanName === 'Garantert' && isUserSudo,
                    }"
                  >
                    {{ translationCreateInvoice }}
                  </button>
                </div>

                <div slot="menu">
                  <hl-DropdownItem :action="() => openNewInvoiceModalInType('rent')"> Husleiefaktura </hl-DropdownItem>

                  <hl-DropdownItem :action="() => openNewInvoiceModalInType('additional')">
                    Tilleggsfaktura
                  </hl-DropdownItem>
                </div>
              </hl-Dropdown>
            </template>

            <router-link :to="{ name: 'tenancy_payments_ledger', params: { rentalObjectId, tenancyId } }">
              <hl-Button variant="primary" outline size="sm" class="ml-3">
                {{ translationTransactions }}
              </hl-Button>
            </router-link>
          </div>
        </div>

        <p class="mt-4">
          Under ser du oversikt over utsendte og fremtidige fakturaer. Fakturaer merket som utkast vil automatisk bli
          sendt ca. 10 dager før forfallsdato, og du kan endre innholdet i de inntil 12 dager før forfall.
        </p>
        
        <TenancyAccountingOrders
          :landlord-id="Number(landlordId)"
          :tenancy-id="Number(tenancyId)"
          :orders="orders"
          :is-fetching-initial-orders="isFetchingInitialOrders"
          :is-order-list-expanded="isOrderListExpanded"
          :is-fetching-subsequent-orders="isFetchingSubsequentOrders"
          class="mt-5"
          @fetch-orders="fetchTenancyOrders"
        />
      </BlockContainer>
    </div>
    <!-- / Payments status -->

    <!-- System Message Modal -->
    <SystemMessageModal
      v-if="showSystemMessageModal"
      :systemMessage="currentSystemMessage"
      @close="showSystemMessageModal = false"
    />
    <!-- / System Message Modal -->
    <div v-if="!isFree" class="mt-4">
      <BlockContainer class="p-5">
        <h5>{{ translationAlerts }}</h5>

        <p class="mt-3">
          Under finner du kopier av betalingspåminnelser som er sendt til leietaker. Betalingspåminnelser på ubetalt
          husleiefaktura sendes automatisk på dag 5 og dag 10 etter forfallsdato. Hvis fakturaen ikke er betalt innen 16
          dager sendes varsel om fravikelse. Her vil du også ha oversikt over varsler om indeksregulering som sendes ut
          til leietaker.
        </p>

        <template v-if="fetchInitialSystemMessages">
          <hl-Loader class="col-12 mt-4 text-center"></hl-Loader>
        </template>
        <template v-else>
          <div v-if="systemMessages" class="mt-5">
            <List v-if="systemMessages.length">
              <ListHeader v-if="!showListsItemsAsCards">
                <div class="col">Dato</div>
                <div class="col">Mottaker</div>
                <div class="col">Emne</div>
                <div class="col text-right">Vis</div>
              </ListHeader>

              <!-- Card View -->
              <div v-for="message in sortedsystemMessages" :key="message.id">
                <ListItemCard v-if="showListsItemsAsCards" :windowWidth="getWindowWidth" class="d-inline-block">
                  <div class="text-center mt-2">
                    <div>
                      <b>
                        <h6 v-if="message.created">
                          <hl-Date :date="message.created"></hl-Date>
                        </h6>
                        <h6 v-else>n/a</h6>
                      </b>
                    </div>
                  </div>

                  <div class="row mt-4 mb-2 py-3">
                    <div class="col">
                      <p class="m-0">
                        <b class="text-secondary">Dato</b>
                      </p>
                      <hl-Date :date="message.created"></hl-Date>
                    </div>

                    <div class="col text-secondary d-none d-md-block">
                      <p class="m-0">
                        <b class="text-secondary">Mottaker</b>
                      </p>
                      {{ message.recipient.substr(0, 30) }}
                    </div>
                  </div>

                  <div class="row mb-2 py-3 align-items-center">
                    <div class="col">
                      <p class="m-0">
                        <b class="text-secondary">Emne</b>
                      </p>

                      {{ message.subject }}
                    </div>
                    <div class="col">
                      <button class="btn btn-sm btn-outline-secondary" @click="setCurrentMessageAndCloseModal(message)">
                        Vis
                      </button>
                    </div>
                  </div>
                </ListItemCard>
                <!-- / Card View -->

                <ListItem v-else>
                  <div class="col">
                    <hl-Date :date="message.created"></hl-Date>
                  </div>
                  <div class="col">{{ message.recipient.substr(0, 30) }}</div>
                  <div class="col">{{ message.subject }}</div>

                  <div class="col text-right">
                    <button class="btn btn-sm btn-outline-secondary" @click="setCurrentMessageAndCloseModal(message)">
                      Vis
                    </button>
                  </div>
                </ListItem>
              </div>
              <div class="text-center mt-5">
                <button class="btn btn-sm btn-outline-secondary" @click="loadMoreSystemMessages">
                  Vis flere <hl-LoaderDots v-if="fetchSubsequentSystemMessages" class="ml-2" />
                </button>
              </div>
            </List>

            <!-- No messages -->
            <div v-else class="mt-5 text-center">
              <img src="@/assets/empty-interest-management.svg" width="140" />
              <h5 class="mt-5">Leieforholdet har ingen systemmeldinger</h5>
            </div>
          </div>
        </template>
      </BlockContainer>
    </div>
    <!-- / System messages -->
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import ActionsMenu from '@/components/UI/ActionsMenu';
import BlockContainer from '@/components/BlockContainer.vue';
import List from '@/components/UI/list/List.vue';
import ListHeader from '@/components/UI/list/ListHeader.vue';
import ListItem from '@/components/UI/list/ListItem.vue';
import ListItemCard from '@/components/UI/list/ListItemCard';
import NewInvoiceModal from '@/components/tenancy/modals/NewInvoiceModal';
import SystemMessageModal from '@/components/tenancy/modals/SystemMessageModal';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Dropdown from '@modules/husleie-framework/src/components/Dropdown.vue';
import DropdownItem from '@modules/husleie-framework/src/components/DropdownItem.vue';
import InfoCard from '@modules/husleie-framework/src/components/layout/cards/InfoCard.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import subscription from '@/mixins/tenancy/subscription';
import UpdateSubscriptionButton from '@/components/upsale/UpdateSubscriptionButton.vue';
import Toast from '@modules/husleie-framework/src/components/Toast.vue';
import TenancyAccountingOrders from '@/components/tenancy/TenancyAccountingOrders.vue';
import { TenancyAccountingApi } from '@modules/husleieapi2';

export default {
  name: 'TenancyPayments',
  mixins: [subscription, i18nCommon],
  components: {
    ActionsMenu,
    BlockContainer,
    List,
    ListHeader,
    ListItem,
    ListItemCard,
    NewInvoiceModal,
    SystemMessageModal,
    UpdateSubscriptionButton,
    TenancyAccountingOrders,
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Dropdown': Dropdown,
    'hl-DropdownItem': DropdownItem,
    'hl-InfoCard': InfoCard,
    'hl-Loader': Loader,
    'hl-LoaderDots': LoaderDots,
    'hl-Date': Date,
    'hl-Currency': Currency,
    'hl-Toast': Toast,
  },

  props: ['rentalObjectId', 'landlordId', 'tenancyId', 'defaultHeader'],

  data() {
    return {
      orders: [],
      isOrderListExpanded: false,
      isFetchingInitialOrders: false,
      isFetchingSubsequentOrders: false,
      newInvoiceType: '',
      showSystemMessageModal: false,
      fetchInitialSystemMessages: false,
      fetchSubsequentSystemMessages: false,
      systemMessages: [],
      currentSystemMessage: {},
      systemMessagesPagination: {
        page: 1,
        limit: 5,
      },
    };
  },
  ORDER_LIMIT: 5,
  computed: {
    ...mapGetters(['getWindowWidth', 'isUserSudo']),
    ...mapGetters('tenancy', [
      'getTenancy',
      'getTenancySettings',
      'isNewInvoiceModalShown',
    ]),

    showListsItemsAsCards() {
      return this.getWindowWidth < 1200;
    },
    sortedsystemMessages() {
      const msgs = [...this.systemMessages];
      msgs.sort((a, b) => b.created.localeCompare(a.created));
      return msgs;
    },
    translationCreateInvoice() {
      return this.translate('rentalObject.tenancy.payments.create new invoice');
    },
    translationTransactions() {
      return this.translate('rentalObject.tenancy.payments.transactions');
    },
    translationAlerts() {
      return this.translate('rentalObject.tenancy.payments.alerts');
    },
  },

  methods: {
    ...mapActions('tenancy', [
      'toggleNewInvoiceModal',
      'fetchTenancyReminders',
    ]),

    async fetchReminders(initialFetch) {
      this.setFetchSystemMessagesSpinnerState(initialFetch, true);
      try {
        this.systemMessages = [
          ...this.systemMessages,
          ...(await this.fetchTenancyReminders({
            landlordId: this.landlordId,
            tenancyId: this.tenancyId,
            data: {
              query: {
                page: this.systemMessagesPagination.page,
                limit: this.systemMessagesPagination.limit,
              },
            },
          })),
        ];
      } catch (err) {
        console.error(err);
        this.$toast('Kunne ikke hente påminnelser.', 'error');
      } finally {
        this.setFetchSystemMessagesSpinnerState(initialFetch, false);
      }
    },

    setFetchSystemMessagesSpinnerState(initialFetch, active) {
      if (initialFetch) {
        this.fetchInitialSystemMessages = active;
      } else {
        this.fetchSubsequentSystemMessages = active;
      }
    },

    loadMoreSystemMessages() {
      ++this.systemMessagesPagination.page;
      this.fetchReminders(false);
    },

    setCurrentMessageAndCloseModal(message) {
      this.currentSystemMessage = message;
      this.showSystemMessageModal = true;
    },

    openNewInvoiceModalInType(type) {
      this.toggleNewInvoiceModal(true);
      this.newInvoiceType = type;
    },

    async fetchOrders() {
      const api = new TenancyAccountingApi();

      try {
        this.orders = await api.getLandlordTenancyAccountingOrders(
          this.landlordId,
          this.tenancyId,
          !this.isOrderListExpanded ? {
            query: {
              limit: this.$options.ORDER_LIMIT,
              page: 1,
            },
          } : undefined,
        );
      } catch {
        this.$toast('Kunne ikke hente fakturaer.', 'error');
      }
    },

    async fetchInitialOrders() {
      this.isFetchingSubsequentOrders = false;
      this.isFetchingInitialOrders = true;

      try {
        await this.fetchOrders();
      } finally {
        this.isFetchingInitialOrders = false;
      }
    },

    async fetchSubsequentOrders() {
      this.isFetchingInitialOrders = false;
      this.isFetchingSubsequentOrders = true;

      try {
        await this.fetchOrders();
      } finally {
        this.isFetchingSubsequentOrders = false;
      }
    },

    async fetchTenancyOrders(isSubsequentFetch) {
      if (isSubsequentFetch) {
        this.isOrderListExpanded = !this.isOrderListExpanded;
        await this.fetchSubsequentOrders();
      } else {
        this.isOrderListExpanded = false;
        await this.fetchInitialOrders();
      }
    },
  },

  async created() {
    await this.fetchTenancyOrders(false);
    await this.fetchReminders(true);
  },
};
</script>

<style lang="scss" scoped>
.upgrade-lead {
  max-width: 475px;
}
.dropdownSudo {
  border: 2px solid #ed2135;
  border-radius: $border-radius;
}
</style>
